import React, { useState, useEffect } from 'react';
import { X, Search, ChevronRight, Wallet, Banknote, Clock, Pencil, Trash2, Check, MessageCircle, Send } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { getCustomers, getCustomerBalance, addBalanceTransaction, updateBalanceTransaction, deleteBalanceTransaction, sendManualSms } from '../services/api';
import { toast } from 'sonner';

const formatPhoneForWhatsApp = (phone) => {
  if (!phone) return '';
  const cleaned = phone.replace(/\D/g, '');
  return cleaned.startsWith('0') ? '90' + cleaned.slice(1) : cleaned.startsWith('90') ? cleaned : '90' + cleaned;
};

const QuickBalanceModal = ({ open, onClose }) => {
  const [step, setStep] = useState(1);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [balanceData, setBalanceData] = useState({ balance: 0, transactions: [] });
  const [loading, setLoading] = useState(false);
  const [newTx, setNewTx] = useState({ amount: '', description: '', type: 'tahsilat' });
  const [editingTxId, setEditingTxId] = useState(null);
  const [editTxForm, setEditTxForm] = useState({ type: '', amount: '', description: '' });

  useEffect(() => {
    if (open) { setStep(1); setQuery(''); setResults([]); setSelectedCustomer(null); }
  }, [open]);

  useEffect(() => {
    if (query.length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      try { const r = await getCustomers(query); setResults(r.data); } catch {}
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  const selectCustomer = async (c) => {
    setSelectedCustomer(c);
    setStep(2);
    setLoading(true);
    try {
      const res = await getCustomerBalance(c.id);
      setBalanceData(res.data);
    } catch { toast.error('Bakiye yüklenemedi'); }
    finally { setLoading(false); }
  };

  const reload = async () => {
    if (!selectedCustomer) return;
    setLoading(true);
    try { const res = await getCustomerBalance(selectedCustomer.id); setBalanceData(res.data); }
    catch {} finally { setLoading(false); }
  };

  const handleAddTx = async (type) => {
    const amount = parseFloat(newTx.amount);
    if (!amount || amount <= 0) { toast.error('Geçerli tutar girin'); return; }
    try {
      await addBalanceTransaction(selectedCustomer.id, { type, amount, description: newTx.description });
      toast.success(type === 'tahsilat' ? 'Tahsilat eklendi' : 'Borç eklendi');
      setNewTx({ amount: '', description: '', type: 'tahsilat' });
      reload();
    } catch { toast.error('İşlem eklenemedi'); }
  };

  const handleEditTx = async (txId) => {
    const amount = parseFloat(editTxForm.amount);
    if (!amount || amount <= 0) { toast.error('Geçerli tutar girin'); return; }
    try {
      await updateBalanceTransaction(selectedCustomer.id, txId, { type: editTxForm.type, amount, description: editTxForm.description });
      toast.success('İşlem güncellendi');
      setEditingTxId(null);
      reload();
    } catch { toast.error('Güncelleme başarısız'); }
  };

  const handleDeleteTx = async (txId) => {
    try {
      await deleteBalanceTransaction(selectedCustomer.id, txId);
      toast.success('İşlem silindi');
      reload();
    } catch { toast.error('Silme başarısız'); }
  };

  const formatCurrency = (v) => new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2 }).format(v) + ' TL';

  const [smsSending, setSmsSending] = useState(false);

  const getBalanceMessage = () => {
    const amount = Math.abs(balanceData.balance);
    const formatted = new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2 }).format(amount) + ' TL';
    const savedUser = JSON.parse(localStorage.getItem('agrosvet_user') || '{}');
    const clinicName = savedUser?.clinic_name || 'Kliniğimiz';
    if (balanceData.balance < 0) {
      return `Sayın ${selectedCustomer?.name}, kliniğimize ${formatted} bakiyeniz bulunmaktadır. Ödeme yapmanızı rica eder, iyi günler dileriz. ${clinicName}`;
    }
    if (balanceData.balance > 0) {
      return `Sayın ${selectedCustomer?.name}, kliniğimizde ${formatted} tutarında alacağınız bulunmaktadır. ${clinicName}`;
    }
    return `Sayın ${selectedCustomer?.name}, kliniğimizdeki bakiyeniz günceldir. İyi günler dileriz. ${clinicName}`;
  };

  const handleBalanceSms = async () => {
    if (smsSending || !selectedCustomer?.phone) return;
    setSmsSending(true);
    try {
      const res = await sendManualSms({
        phone: selectedCustomer.phone,
        message: getBalanceMessage(),
        customer_id: selectedCustomer.id,
        customer_name: selectedCustomer.name,
        sms_type: 'manual_balance',
      });
      if (res.data?.success) {
        toast.success('Bakiye hatırlatma SMS gönderildi');
      } else {
        toast.error('SMS gönderilemedi');
      }
    } catch {
      toast.error('SMS gönderilemedi');
    } finally {
      setSmsSending(false);
    }
  };

  const handleBalanceWhatsApp = () => {
    if (!selectedCustomer?.phone) return;
    const waPhone = formatPhoneForWhatsApp(selectedCustomer.phone);
    const text = encodeURIComponent(getBalanceMessage());
    window.open(`https://wa.me/${waPhone}?text=${text}`, '_blank');
  };

  if (!open) return null;

  return (
    <div className="fixed inset-x-0 top-0 bottom-[65px] z-[9998] flex sm:inset-0 sm:items-center sm:justify-center sm:p-3" data-testid="quick-balance-modal">
      <div className="absolute inset-0 bg-black/40" onClick={() => onClose()} />
      <div className="relative bg-white sm:rounded-2xl w-full sm:max-w-md lg:max-w-3xl flex flex-col h-full sm:h-auto sm:max-h-[75vh] overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            {step === 2 && (
              <button onClick={() => { setStep(1); setSelectedCustomer(null); }} className="p-1.5 rounded-full hover:bg-gray-100">
                <ChevronRight size={18} className="text-[#8E8E93] rotate-180" />
              </button>
            )}
            <h2 className="text-lg font-bold text-gray-900">
              {step === 1 ? 'Müşteri Seçin' : `${selectedCustomer?.name}`}
            </h2>
          </div>
          <button onClick={() => onClose()} className="p-2 rounded-full hover:bg-gray-100" data-testid="close-balance-modal">
            <X size={20} className="text-gray-400" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {step === 1 && (
            <>
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8E8E93]" />
                <Input placeholder="Müşteri Ara (İsim, telefon veya TCKN)" value={query} onChange={e => setQuery(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && results.length === 1) { selectCustomer(results[0]); } }}
                  className="h-11 pl-9 text-sm bg-[#F2F2F7] border-0 rounded-xl" autoFocus data-testid="balance-customer-search" />
              </div>
              {results.map(c => (
                <button key={c.id} onClick={() => selectCustomer(c)}
                  className="w-full flex items-center justify-between px-3 py-2.5 bg-[#F2F2F7] rounded-xl hover:bg-gray-200 text-left"
                  data-testid={`balance-customer-${c.id}`}>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{c.name}</p>
                    <p className="text-xs text-[#8E8E93]">{c.phone}</p>
                  </div>
                  <ChevronRight size={18} className="text-[#C7C7CC]" />
                </button>
              ))}
            </>
          )}

          {step === 2 && (
            <>
              {/* Current Balance */}
              <div className={`text-center py-4 rounded-2xl ${balanceData.balance < 0 ? 'bg-red-50' : balanceData.balance > 0 ? 'bg-green-50' : 'bg-[#F2F2F7]'}`}>
                <p className="text-xs text-[#8E8E93] uppercase tracking-wider mb-1">Mevcut Bakiye</p>
                <p className={`text-3xl font-bold ${balanceData.balance < 0 ? 'text-[#FF3B30]' : balanceData.balance > 0 ? 'text-[#34C759]' : 'text-gray-900'}`} data-testid="quick-balance-amount">
                  {balanceData.balance < 0 ? `-${formatCurrency(Math.abs(balanceData.balance))}` : balanceData.balance > 0 ? `+${formatCurrency(balanceData.balance)}` : '0 TL'}
                </p>
                <p className="text-xs text-[#8E8E93] mt-1">
                  {balanceData.balance < 0 ? 'Müşteri Borçlu' : balanceData.balance > 0 ? 'Müşteri Alacaklı' : ''}
                </p>
              </div>

              {/* SMS / WhatsApp - only when in debt */}
              {Number(balanceData.balance) < 0 && selectedCustomer?.phone && (
                <div className="flex items-center justify-center gap-2" data-testid="balance-notification-actions">
                  <button
                    onClick={handleBalanceSms}
                    disabled={smsSending}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-blue-50 hover:bg-blue-100 text-blue-700 text-sm font-medium transition-colors disabled:opacity-50 border border-blue-200"
                    data-testid="balance-sms-btn"
                  >
                    <MessageCircle size={15} />
                    SMS Gönder
                  </button>
                  <button
                    onClick={handleBalanceWhatsApp}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-green-50 hover:bg-green-100 text-green-700 text-sm font-medium transition-colors border border-green-200"
                    data-testid="balance-whatsapp-btn"
                  >
                    <Send size={15} />
                    WhatsApp
                  </button>
                </div>
              )}

              {/* Quick Add */}
              <div className="bg-[#F2F2F7] rounded-2xl p-4 space-y-3">
                <div className="relative">
                  <Input type="number" value={newTx.amount} onChange={(e) => setNewTx(p => ({ ...p, amount: e.target.value }))}
                    placeholder="Tutar" className="h-12 bg-white border-0 rounded-xl pr-8 text-base" data-testid="quick-tx-amount" />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#8E8E93]">TL</span>
                </div>
                <Input value={newTx.description} onChange={(e) => setNewTx(p => ({ ...p, description: e.target.value }))}
                  placeholder="Açıklama (opsiyonel)" className="h-10 bg-white border-0 rounded-xl text-sm" data-testid="quick-tx-description" />
                <div className="grid grid-cols-2 gap-3">
                  <Button onClick={() => handleAddTx('tahsilat')} disabled={!newTx.amount}
                    className="h-11 rounded-xl bg-[#34C759] hover:bg-green-600 text-sm font-semibold disabled:opacity-40" data-testid="quick-add-tahsilat">
                    <Banknote size={16} className="mr-1.5" /> Tahsilat
                  </Button>
                  <Button onClick={() => handleAddTx('borc')} disabled={!newTx.amount}
                    className="h-11 rounded-xl bg-[#FF3B30] hover:bg-red-600 text-sm font-semibold disabled:opacity-40" data-testid="quick-add-borc">
                    <Clock size={16} className="mr-1.5" /> Borç
                  </Button>
                </div>
              </div>

              {/* Transaction History */}
              <div>
                <p className="text-xs font-semibold text-[#8E8E93] uppercase tracking-wider mb-3">İşlem Geçmişi</p>
                {loading ? (
                  <div className="py-6 flex justify-center">
                    <div className="w-6 h-6 border-2 border-[#6b6de3] border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : balanceData.transactions.length > 0 ? (
                  <div className="space-y-2">
                    {balanceData.transactions.map((tx) => (
                      <div key={tx.id} className="bg-[#F2F2F7] rounded-xl p-3" data-testid={`quick-tx-${tx.id}`}>
                        {editingTxId === tx.id ? (
                          <div className="space-y-2">
                            <div className="grid grid-cols-2 gap-2">
                              <button onClick={() => setEditTxForm(p => ({ ...p, type: 'tahsilat' }))}
                                className={`py-2 rounded-lg text-xs font-medium ${editTxForm.type === 'tahsilat' ? 'bg-[#34C759] text-white' : 'bg-white text-gray-700'}`}>
                                Tahsilat
                              </button>
                              <button onClick={() => setEditTxForm(p => ({ ...p, type: 'borc' }))}
                                className={`py-2 rounded-lg text-xs font-medium ${editTxForm.type === 'borc' ? 'bg-[#FF3B30] text-white' : 'bg-white text-gray-700'}`}>
                                Borç
                              </button>
                            </div>
                            <Input type="number" value={editTxForm.amount} onChange={e => setEditTxForm(p => ({ ...p, amount: e.target.value }))} className="h-9 bg-white border-0 rounded-lg text-sm" />
                            <Input value={editTxForm.description} onChange={e => setEditTxForm(p => ({ ...p, description: e.target.value }))} placeholder="Açıklama" className="h-9 bg-white border-0 rounded-lg text-sm" />
                            <div className="flex gap-2">
                              <Button size="sm" onClick={() => handleEditTx(tx.id)} className="bg-[#34C759] hover:bg-green-600 rounded-full h-8 px-3 text-xs flex-1">
                                <Check size={12} className="mr-1" /> Kaydet
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => setEditingTxId(null)} className="rounded-full h-8 px-3 text-xs">
                                Vazgeç
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="flex items-center gap-2">
                                <span className={`text-sm font-bold ${tx.type === 'tahsilat' ? 'text-[#34C759]' : 'text-[#FF3B30]'}`}>
                                  {tx.type === 'tahsilat' ? '+' : '-'}{formatCurrency(tx.amount)}
                                </span>
                                <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${tx.type === 'tahsilat' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                  {tx.type === 'tahsilat' ? 'Tahsilat' : 'Borç'}
                                </span>
                              </div>
                              {tx.description && <p className="text-xs text-[#8E8E93] mt-0.5">{tx.description}</p>}
                              <p className="text-[10px] text-[#C7C7CC] mt-0.5">{new Date(tx.created_at).toLocaleDateString('tr-TR')}</p>
                            </div>
                            <div className="flex gap-1">
                              <button onClick={() => { setEditingTxId(tx.id); setEditTxForm({ type: tx.type, amount: tx.amount, description: tx.description || '' }); }}
                                className="p-1.5 rounded-full hover:bg-white"><Pencil size={13} className="text-[#8E8E93]" /></button>
                              <button onClick={() => handleDeleteTx(tx.id)}
                                className="p-1.5 rounded-full hover:bg-white"><Trash2 size={13} className="text-[#FF3B30]" /></button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-[#8E8E93] text-center py-4">Henüz işlem yok</p>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuickBalanceModal;
